import { Group, Card, Text, Box, Avatar, Stack, Skeleton } from '@mantine/core';
import { PageRoutes } from '../../../../constants/routes';
import {
  getProfilePicture,
  UserDocument,
} from '../../../../types/UserDocument';
import { primaryColor } from '../../../../constants/theme';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState } from 'react';

export const UserCard = ({ userDocument }: { userDocument: UserDocument }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const professionalId = searchParams.get('professionalId');
  const [hovered, setHovered] = useState(false);

  const handleClick = () => {
    navigate(`${PageRoutes.PROFILE}?professionalId=${userDocument.id}`, {
      state: {
        ownProfile: false,
        fromPeopleOfInterest: true,
      },
    });
  };

  const isSelected = professionalId === userDocument.id;
  const currentBorderColor = isSelected || hovered ? primaryColor : 'var(--mantine-color-gray-3)';

  return (
    <Card
      shadow="sm"
      padding="lg"
      radius="md"
      withBorder
      h="100%"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderColor: currentBorderColor,
        cursor: 'pointer',
        borderWidth: isSelected ? 3 : 1,
        transition: 'border-color 0.2s ease',
      }}
      onClick={handleClick}
      data-testid={UserCardTestIds.USER_CARD}
    >
      <Group mb="md" wrap="nowrap">
        <Avatar
          size="lg"
          radius="xl"
          src={getProfilePicture(userDocument.profilePicture)}
          data-testid={UserCardTestIds.USER_CARD_AVATAR}
        />
        <Box>
          <Text
            fw={500}
            size="sm"
            ta="start"
            data-testid={UserCardTestIds.USER_CARD_NAME}
          >
            {userDocument.isProfessional
              ? userDocument.displayName
              : userDocument.fullName}
          </Text>
          <Text
            size="xs"
            c="dimmed"
            ta="start"
            data-testid={UserCardTestIds.USER_CARD_HEADLINE}
          >
            {userDocument.headline}
          </Text>
        </Box>
      </Group>
      <Text
        size="xs"
        c="dimmed"
        ta="start"
        lineClamp={4}
        data-testid={UserCardTestIds.USER_CARD_ABOUT}
      >
        {userDocument.headerBio}
      </Text>
    </Card>
  );
};

export const UserCardSkeletonLoader = () => {
  return (
    <Card
      shadow="sm"
      padding="lg"
      radius="md"
      withBorder
      h="100%"
      style={{ borderColor: 'var(--mantine-color-gray-3)' }}
      data-testid={UserCardTestIds.LOADER_SKELETON}
    >
      <Group mb="md">
        <Skeleton circle height={40} width={40} />
        <Skeleton height={10} radius="sm" width="70%" />
      </Group>
      <Stack>
        <Skeleton height={10} radius="xl" width="90%" />
        <Skeleton height={10} radius="xl" width="90%" />
        <Skeleton height={10} radius="xl" width="90%" />
        <Skeleton height={10} radius="xl" width="60%" />
      </Stack>
    </Card>
  );
};

export const UserCardTestIds = {
  USER_CARD: 'user-card',
  USER_CARD_AVATAR: 'user-card-avatar',
  USER_CARD_NAME: 'user-card-name',
  USER_CARD_HEADLINE: 'user-card-headline',
  USER_CARD_ABOUT: 'user-card-about',
  LOADER_SKELETON: 'user-loader-skeleton',
};
